<template>
  <div>
    <!-- Purchase Button with responsive display -->
    <Button
      :icon="icon || 'pi pi-ticket'"
      class="purchase-button"
      :class="[
        props.fullWidth ? 'w-full' : '',
        props.buttonClass,
        props.buttonIconOnly ? 'icon-only-button' : 'responsive-button',
        props.buttonSize === 'small' ? 'p-button-sm' : '',
        props.noMinWidth ? 'no-min-width' : '',
      ]"
      :label="props.buttonIconOnly ? undefined : buttonLabel"
      :loading="isLoading"
      :severity="buttonSeverity"
      @click="openCheckoutModal"
    />

    <!-- Checkout Modal Dialog -->
    <Dialog
      v-model:visible="showCheckoutModal"
      :modal="true"
      :dismissable-mask="false"
      :closable="true"
      append-to="body"
      :style="{ width: '90vw', maxWidth: '1200px', maxHeight: '90vh' }"
      :header="modalTitle"
      @hide="handleModalHide"
    >
      <div
        v-if="isLoading"
        class="flex justify-center items-center p-8"
      >
        <ProgressSpinner />
        <span class="ml-4">Loading checkout...</span>
      </div>

      <div
        v-else-if="error"
        class="p-4 text-red-500"
      >
        <div class="mb-4">
          {{ error }}
        </div>
        <Button
          label="Try Again"
          @click="retryCheckout"
        />
      </div>

      <!-- Eventbrite Widget Container -->
      <div
        v-else
        :id="containerId"
        class="min-h-[600px]"
      />

      <template #footer>
        <Button
          label="Close"
          severity="secondary"
          class="p-button-outlined"
          @click="showCheckoutModal = false"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import { computed, nextTick, onMounted, ref } from 'vue'
import { TICKET_TYPES, TICKET_TYPE_INFO, useEventbriteService } from '~/composables/useEventbriteService'
import { createEmbeddedCheckout, ensureEventbriteScriptLoaded } from '~/composables/useEventbriteWidget'

// Define the ticket type as a proper union type
type TicketType = typeof TICKET_TYPES[keyof typeof TICKET_TYPES]

// Options for checkout session generation
interface GenerateCheckoutSessionOptions {
  childId?: string
  childrenIds?: string[]
  subkreweId?: string
}

const props = defineProps({
  /**
   * Type of ticket to purchase
   * Allowed values: 'membership', 'vip', 'parking', 'children'
   */
  ticketType: {
    type: String as PropType<TicketType>,
    required: true,
    validator: (value: string): boolean =>
      Object.values(TICKET_TYPES).includes(value as TicketType),
  },

  /**
   * Custom button label (optional)
   * If not provided, a default label based on ticket type will be used
   */
  buttonLabel: {
    type: String,
    default: '',
  },

  /**
   * PrimeVue button severity
   */
  buttonSeverity: {
    type: String,
    default: 'primary',
  },

  /**
   * Make button take full width of container
   */
  fullWidth: {
    type: Boolean,
    default: false,
  },

  /**
   * Custom icon for the button
   */
  icon: {
    type: String,
    default: 'pi pi-dollar',
  },

  /**
   * Button size (small, normal, large)
   */
  buttonSize: {
    type: String,
    default: 'normal',
  },

  /**
   * Show only the icon on all screen sizes
   */
  buttonIconOnly: {
    type: Boolean,
    default: false,
  },

  /**
   * Button style class
   */
  buttonClass: {
    type: String,
    default: '',
  },

  // For child tickets - ID of a single child to purchase ticket for
  childId: {
    type: String,
    default: '',
  },

  // For child tickets - IDs of multiple children to purchase tickets for
  childrenIds: {
    type: Array as PropType<string[]>,
    default: () => [],
  },

  // For bulk purchases - SubKrewe context
  subkreweId: {
    type: String,
    default: '',
  },

  // Whether to automatically close the dialog after a successful order
  closeOnSuccess: {
    type: Boolean,
    default: false,
  },

  // Opt-out of enforced minimum width (used for cramped toolbars / mobile)
  noMinWidth: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['order-complete', 'error', 'checkout-opened', 'checkout-closed'])

// Component state
const showCheckoutModal = ref(false)
const isLoading = ref(false)
const accessCode = ref('')
const error = ref('')
const orderId = ref('')
const updatingStatus = ref(false)
const statusUpdateError = ref('')
const eventbriteEventId = useRuntimeConfig().public.eventbriteEventId
// Unique container ID per component instance to avoid conflicts with multiple PurchaseButtons
const containerId = `eb-checkout-${Math.random().toString(36).slice(2, 9)}`
// HARDENING NOTES:
// 1. Unified handleOrderComplete consolidates duplicate code paths (modal & embedded) to prevent divergence.
// 2. Adds up to 3 retry attempts with incremental backoff (200ms, 400ms) for backend status persistence.
// 3. Server now idempotent; repeated calls safe if first attempt races with script / network issues.
// 4. Emits detailed error event if persistence ultimately fails so parent components can react.
// 5. Avoids wiping order state until modal close; prevents race where state reset before retries finish.

// Service for Eventbrite API
const { generateCheckoutSession } = useEventbriteService()

// Get button label based on ticket type
const defaultButtonLabels = {
  [TICKET_TYPES.MEMBERSHIP]: 'Pay Dues',
  [TICKET_TYPES.VIP]: 'Chewbacchanal VIP Upgrade',
  [TICKET_TYPES.PARKING]: 'Buy Contraption Parking',
  [TICKET_TYPES.CHILDREN]: 'Pay Children Membership Dues',
  [TICKET_TYPES.BULK]: 'Buy Multiple Memberships',
}

// Computed properties
const buttonLabel = computed(() => {
  return props.buttonLabel || defaultButtonLabels[props.ticketType as TicketType] || 'Purchase Ticket'
})

const modalTitle = computed(() => {
  const ticketInfo = TICKET_TYPE_INFO[props.ticketType as TicketType]
  return ticketInfo ? `${ticketInfo.label} Checkout` : 'Checkout'
})

// Ensure the Eventbrite widget script is loaded
// Script loading now centralized in composable

/**
 * Handles opening the checkout modal and generating a checkout session
 */
async function openCheckoutModal() {
  // Always use embedded checkout - the native Eventbrite modal has issues on mobile
  // where programmatic clicks on hidden elements are blocked
  showCheckoutModal.value = true
  isLoading.value = true
  error.value = ''
  accessCode.value = ''
  // Wait a tick so the checkout dialog is mounted before signaling parent to close
  await nextTick()
  emit('checkout-opened')

  try {
    // Wait a moment to ensure the modal is visible
    await nextTick()

    // Ensure the Eventbrite script is loaded
    await ensureEventbriteScriptLoaded()
    // Build options for checkout session based on ticket type
    const options: GenerateCheckoutSessionOptions = {}

    // For child tickets, add childId or childrenIds if provided
    if (props.ticketType === TICKET_TYPES.CHILDREN) {
      if (props.childId) {
        options.childId = props.childId
      }
      else if (props.childrenIds && props.childrenIds.length > 0) {
        options.childrenIds = props.childrenIds
      }
    }
    console.warn(`Creating checkout session for ${props.ticketType} with options:`, options)

    // Generate checkout session (user ID is retrieved on the server from session)
    if (props.ticketType === TICKET_TYPES.BULK) {
      if (!props.subkreweId) throw new Error('Missing subkreweId for bulk purchase')
      options.subkreweId = props.subkreweId
    }

    const result = await generateCheckoutSession(props.ticketType as TicketType, options)

    if (!result.success) {
      throw new Error(result.message || 'Failed to create checkout session')
    }

    accessCode.value = result.code || ''

    // Create the embedded checkout widget
    await nextTick()
    createEmbeddedCheckoutWidget()
  }
  catch (e) {
    error.value = e instanceof Error ? e.message : 'An error occurred'
    emit('error', { error: error.value })
  }
  finally {
    isLoading.value = false
  }
}

/**
 * Retries the checkout process
 */
function retryCheckout() {
  error.value = ''
  openCheckoutModal()
}

/**
 * Unified order completion handler with retry/backoff and idempotent server call.
 */
async function handleOrderComplete(completedOrderId: string, raw: unknown) {
  orderId.value = completedOrderId
  updatingStatus.value = true
  statusUpdateError.value = ''
  const MAX_ATTEMPTS = 3
  let attempt = 0
  let success = false
  let lastErr: unknown = null
  while (attempt < MAX_ATTEMPTS && !success) {
    attempt += 1
    try {
      if (props.ticketType === TICKET_TYPES.BULK) {
        await $fetch('/api/eventbrite/bulk-mint', { method: 'POST', body: { accessCode: accessCode.value, orderId: orderId.value } })
      }
      else {
        interface UpdateStatusResponse {
          error?: boolean
          message?: string
          success?: boolean
          alreadyProcessed?: boolean
        }
        const res = await $fetch<UpdateStatusResponse>('/api/eventbrite/update-order-status', {
          method: 'POST',
          body: { accessCode: accessCode.value, orderId: orderId.value, ticketType: props.ticketType },
        })
        if (res.error) throw new Error(res.message || 'Status update failed')
      }
      success = true
    }
    catch (e) {
      lastErr = e
      console.warn(`Order status update attempt ${attempt} failed`, e)
      if (attempt < MAX_ATTEMPTS) await new Promise(r => setTimeout(r, attempt * 200))
    }
  }
  updatingStatus.value = false
  if (!success) {
    statusUpdateError.value = lastErr instanceof Error ? lastErr.message : 'Failed to record order'
    emit('error', { error: statusUpdateError.value, phase: 'post-purchase-status-update' })
  }
  else {
    // Track successful purchase in PostHog
    const { $posthog } = useNuxtApp()
    $posthog()?.capture('purchase_completed', {
      ticket_type: props.ticketType,
      order_id: completedOrderId,
    })
  }
  emit('order-complete', { orderId: raw, ticketType: props.ticketType, accessCode: accessCode.value, statusPersisted: success })
  if (success && props.closeOnSuccess) {
    // Only close automatically if explicitly enabled
    showCheckoutModal.value = false
  }
}

/**
 * Creates the embedded checkout widget
 */
function createEmbeddedCheckoutWidget() {
  if (!accessCode.value) {
    return
  }

  console.warn('Creating embedded checkout widget with access code:', accessCode.value)

  // Wait a brief moment to ensure the container is ready
  setTimeout(() => {
    try {
      createEmbeddedCheckout({
        eventId: eventbriteEventId,
        promoCode: accessCode.value,
        containerId: containerId,
        containerHeight: 600,
        onOrderComplete: async ({ orderId: completedOrderId, raw }) => {
          console.warn('Order completed successfully!', raw)
          await handleOrderComplete(completedOrderId, raw)
        },
      })
    }
    catch (e) {
      console.error('Error creating widget:', e)
      error.value = 'Failed to create checkout widget. Please try again.'
    }
  }, 100)
}

/**
 * Handles the modal being hidden
 */
function handleModalHide() {
  emit('checkout-closed', {
    completed: !!orderId.value,
    orderId: orderId.value,
    ticketType: props.ticketType,
    accessCode: accessCode.value,
    statusUpdateError: statusUpdateError.value,
  })

  // Reset state
  accessCode.value = ''
  orderId.value = ''
  statusUpdateError.value = ''
  updatingStatus.value = false
}

// Pre-load the script when the component is mounted
onMounted(() => {
  if (typeof window !== 'undefined') {
    // Don't await, just start loading in the background
    ensureEventbriteScriptLoaded().catch((e) => {
      console.warn('Failed to preload Eventbrite script:', e)
    })
  }
})

// Expose an imperative API so parents can trigger checkout without relying on a click
defineExpose({
  open: openCheckoutModal,
})
</script>

<style scoped>
.purchase-button {
  min-width: 100px;
}

/* Smaller minimum width for small buttons */
.purchase-button.p-button-sm {
  min-width: 100px;
}

/* Allow opt-out when space constrained */
.purchase-button.no-min-width {
  min-width: auto !important;
  width: auto !important;
}

.responsive-button {
  display: flex;
  justify-content: center;
  align-items: center;
}

.icon-only-button {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  border-radius: 50% !important;
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  min-height: 36px !important;
  padding: 0 !important;
  aspect-ratio: 1/1 !important;
}

@media (max-width: 768px) {
  .responsive-button {
    justify-content: center;
    align-items: center;
  }

  .responsive-button .p-button-label {
    display: none;
  }
}
</style>
